"use client";

import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import ConsoleGuide from "@/components/ConsoleGuide";
import IndexBadges from "@/components/IndexBadges";

type EMAStatus = {
  value: number;
  status: "Above" | "Below" | "On";
  distance_pct: number;
};

type EMARow = {
  symbol: string;
  name?: string;
  sector?: string;
  price: number;
  date?: string | null;
  ema: Record<string, EMAStatus>;
  indices?: string[];
};

type EMAResponse = {
  results: EMARow[];
  summary: Record<string, { above: number; below: number; on: number }>;
  ema_periods: number[];
  timeframe: string;
  scan_date: string;
  total_symbols: number;
  tolerance_pct: number;
};

type StatusFilter = "all" | "above" | "below" | "on";

const TIMEFRAMES = ["daily", "weekly", "monthly"] as const;
const LIMIT_OPTIONS = [10, 25, 50, 100] as const;
const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "above", label: "Above" },
  { value: "below", label: "Below" },
  { value: "on", label: "On EMA" },
] as const;
// Default periods must match the snapshot on the server to get instant results.
// Snapshot is generated with [9, 21, 44, 100, 200] — keep in sync with the job.
const DEFAULT_EMA_PERIODS = [9, 21, 44, 100, 200];

const formatNumber = (value: number, digits = 2) =>
  value.toLocaleString(undefined, { maximumFractionDigits: digits });


const formatDate = (value?: string | null) => {
  if (!value) return "N/A";
  return new Date(value).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const statusStyle = (status: EMAStatus["status"]) => {
  if (status === "Above") {
    return {
      color: "var(--color-success)",
      bg: "rgba(16, 185, 129, 0.16)",
      border: "rgba(16, 185, 129, 0.35)",
    };
  }
  if (status === "Below") {
    return {
      color: "var(--color-danger)",
      bg: "rgba(239, 68, 68, 0.16)",
      border: "rgba(239, 68, 68, 0.35)",
    };
  }
  return {
    color: "#f59e0b",
    bg: "rgba(245, 158, 11, 0.16)",
    border: "rgba(245, 158, 11, 0.35)",
  };
};

type DropdownOption = { value: string; label: string };

const dropdownButtonStyle: CSSProperties = {
  width: "100%",
  padding: "0.55rem 0.9rem",
  borderRadius: "10px",
  border: "1px solid rgba(148, 163, 184, 0.3)",
  background: "rgba(15, 23, 42, 0.4)",
  color: "var(--color-text)",
  fontWeight: 600,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "0.5rem",
  cursor: "pointer",
};

const dropdownMenuStyle: CSSProperties = {
  position: "absolute",
  top: "calc(100% + 6px)",
  left: 0,
  right: 0,
  background: "rgba(15, 23, 42, 0.98)",
  border: "1px solid rgba(148, 163, 184, 0.2)",
  borderRadius: "10px",
  boxShadow: "0 18px 30px rgba(5, 8, 15, 0.45)",
  zIndex: 20,
  padding: "0.35rem",
};

const dropdownOptionStyle: CSSProperties = {
  width: "100%",
  textAlign: "left",
  padding: "0.5rem 0.65rem",
  borderRadius: "8px",
  background: "transparent",
  border: "none",
  color: "var(--color-text)",
  cursor: "pointer",
  fontWeight: 600,
};

const dropdownOptionActiveStyle: CSSProperties = {
  background: "rgba(59, 130, 246, 0.18)",
  color: "#bfdbfe",
};

function Dropdown({
  value,
  options,
  onChange,
  ariaLabel,
}: {
  value: string;
  options: DropdownOption[];
  onChange: (next: string) => void;
  ariaLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const selected = options.find((option) => option.value === value) ?? options[0];

  useEffect(() => {
    if (!open) return;
    const handleClick = (event: MouseEvent) => {
      if (!containerRef.current) return;
      if (event.target instanceof Node && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    window.addEventListener("mousedown", handleClick);
    return () => window.removeEventListener("mousedown", handleClick);
  }, [open]);

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((prev) => !prev)}
        style={dropdownButtonStyle}
      >
        <span>{selected?.label ?? value}</span>
        <span aria-hidden="true" style={{ opacity: 0.7 }}>
          ▾
        </span>
      </button>
      {open && (
        <div role="listbox" style={dropdownMenuStyle}>
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={option.value === value}
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
              style={{
                ...dropdownOptionStyle,
                ...(option.value === value ? dropdownOptionActiveStyle : null),
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function TrendsTrackerPage() {
  const [timeframe, setTimeframe] = useState<(typeof TIMEFRAMES)[number]>("daily");
  const [data, setData] = useState<EMAResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<Record<string, StatusFilter>>({});
  const [limit, setLimit] = useState<number>(25);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const activePeriods = data?.ema_periods ?? DEFAULT_EMA_PERIODS;

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch("/api/trend/ema-status", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ema_periods: DEFAULT_EMA_PERIODS,
            timeframe,
            use_snapshot: true,
          }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const payload = (await res.json()) as EMAResponse;
        setData(payload);
        setFilters((prev) => {
          const nextFilters: Record<string, StatusFilter> = {};
          payload.ema_periods.forEach((period) => {
            nextFilters[period.toString()] = prev[period.toString()] ?? "all";
          });
          return nextFilters;
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load EMA status");
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [timeframe]);

  useEffect(() => {
    setPage(1);
  }, [timeframe]);

  const reportDate = useMemo(() => {
    return data?.scan_date ? formatDate(data.scan_date) : "N/A";
  }, [data?.scan_date]);

  const filteredRows = useMemo(() => {
    if (!data) return [];
    const term = search.trim().toUpperCase();
    return data.results
      .filter((row) => {
        if (term && !row.symbol.includes(term)) return false;
        return data.ema_periods.every((period) => {
          const key = period.toString();
          const filter = filters[key] ?? "all";
          if (filter === "all") return true;
          const status = row.ema[key]?.status?.toLowerCase();
          return status === filter;
        });
      });
  }, [data, filters, search]);

  const totalPages = useMemo(() => {
    if (!filteredRows.length) return 1;
    return Math.max(1, Math.ceil(filteredRows.length / limit));
  }, [filteredRows.length, limit]);

  const pagedRows = useMemo(() => {
    const safePage = Math.min(Math.max(1, page), totalPages);
    const start = (safePage - 1) * limit;
    return filteredRows.slice(start, start + limit);
  }, [filteredRows, limit, page, totalPages]);

  const currentFocusLine = data
    ? `${timeframe.charAt(0).toUpperCase() + timeframe.slice(1)} EMA scan across ${formatNumber(data.total_symbols, 0)} symbols with ${activePeriods.join(" / ")} in focus.`
    : "Loading the latest EMA scan.";

  return (
    <>
      <section className="page-header page-header--accent">
        <div className="page-header-container">
          <h1 className="page-title">EMA Scanner</h1>
          <p className="page-subtitle">Narrow the market into structured trend states before reviewing full chart setups.</p>
        </div>
      </section>

      <section className="strategy-detail-section">
        <div className="strategy-detail-container">
          <ConsoleGuide
            usageTitle="Filter first, inspect later"
            usageText="Choose the timeframe, set the EMA playbook, then filter the states you care about. This page should adapt to your trade plan without forcing a trip back to settings."
            focusTitle="EMA Scanner"
            focusText={currentFocusLine}
            style={{ marginBottom: "1.5rem" }}
          />

          <div
            style={{
              background: "linear-gradient(135deg, rgba(26,34,44,0.95), rgba(15,23,32,0.90))",
              borderRadius: "16px",
              border: "1px solid rgba(148, 163, 184, 0.2)",
              padding: "1.5rem",
              marginBottom: "1.5rem",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
              <div style={{ fontWeight: 700, fontSize: "1.15rem" }}>Report Date: {reportDate}</div>
            </div>

            <div className="market-intel-reading-grid" style={{ marginTop: "1rem" }}>
              <div className="market-intel-reading-card">
                <div className="market-intel-reading-label">Step 1</div>
                <strong>Choose timeframe</strong>
                <span>Daily catches shorter rotations, weekly shows cleaner structure, monthly isolates the broad trend.</span>
              </div>
              <div className="market-intel-reading-card">
                <div className="market-intel-reading-label">Step 2</div>
                <strong>Set the stack</strong>
                <span>Switch between fast, core, or custom EMA pairs without leaving the scanner results page.</span>
              </div>
              <div className="market-intel-reading-card">
                <div className="market-intel-reading-label">Step 3</div>
                <strong>Review survivors</strong>
                <span>Use the final list as a shortlist, not a signal by itself. The symbol chart still decides the trade.</span>
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: "1rem",
                marginTop: "1rem",
              }}
            >
              {data?.ema_periods?.map((period) => {
                const stats = data.summary?.[period.toString()] ?? { above: 0, below: 0, on: 0 };
                return (
                  <div
                    key={period}
                    style={{
                      borderRadius: "14px",
                      border: "1px solid rgba(148, 163, 184, 0.2)",
                      padding: "1rem",
                      background: "rgba(15, 23, 42, 0.6)",
                      display: "grid",
                      gap: "0.35rem",
                    }}
                  >
                    <div style={{ fontWeight: 700 }}>EMA {period}</div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.9rem" }}>
                      <span style={{ color: "var(--color-success)" }}>Above</span>
                      <span>{stats.above}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.9rem" }}>
                      <span style={{ color: "var(--color-danger)" }}>Below</span>
                      <span>{stats.below}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.9rem" }}>
                      <span style={{ color: "#f59e0b" }}>On</span>
                      <span>{stats.on}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(180px, 1fr))",
              gap: "0.75rem",
              marginBottom: "1.25rem",
            }}
          >
            {TIMEFRAMES.map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setTimeframe(value)}
                style={{
                  padding: "0.75rem 1rem",
                  borderRadius: "12px",
                  border: value === timeframe ? "1px solid #3b82f6" : "1px solid rgba(148, 163, 184, 0.3)",
                  background: value === timeframe ? "rgba(59, 130, 246, 0.18)" : "rgba(15, 23, 42, 0.5)",
                  color: "var(--color-text)",
                  fontWeight: 700,
                  textTransform: "capitalize",
                  cursor: "pointer",
                }}
              >
                {value}
              </button>
            ))}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "1rem",
              marginBottom: "1.5rem",
            }}
          >

            {activePeriods.map((ema) => (
              <div key={ema}>
                <label style={{ display: "block", fontSize: "0.85rem", fontWeight: 600, marginBottom: "0.4rem" }}>
                  EMA {ema}
                </label>
                <Dropdown
                  value={filters[ema.toString()] ?? "all"}
                  ariaLabel={`Filter EMA ${ema}`}
                  options={STATUS_OPTIONS.map((option) => ({ value: option.value, label: option.label }))}
                  onChange={(next) => {
                    setFilters((prev) => ({ ...prev, [ema.toString()]: next as StatusFilter }));
                    setPage(1);
                  }}
                />
              </div>
            ))}

            <div>
              <label style={{ display: "block", fontSize: "0.85rem", fontWeight: 600, marginBottom: "0.4rem" }}>
                Show
              </label>
              <Dropdown
                value={limit.toString()}
                ariaLabel="Rows per page"
                options={LIMIT_OPTIONS.map((value) => ({ value: value.toString(), label: value.toString() }))}
                onChange={(next) => {
                  setLimit(Number(next));
                  setPage(1);
                }}
              />
            </div>

            <div>
              <label style={{ display: "block", fontSize: "0.85rem", fontWeight: 600, marginBottom: "0.4rem" }}>
                Search
              </label>
              <input
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                placeholder="Search symbol"
                style={{
                  width: "100%",
                  padding: "0.55rem 0.9rem",
                  borderRadius: "10px",
                  border: "1px solid rgba(148, 163, 184, 0.3)",
                  background: "rgba(15, 23, 42, 0.4)",
                  color: "var(--color-text)",
                  fontWeight: 600,
                }}
              />
            </div>
          </div>

          {loading && (
            <div style={{ padding: "2rem", textAlign: "center", color: "var(--color-text-muted)" }}>
              Loading EMA data...
            </div>
          )}

          {error && (
            <div
              style={{
                padding: "1rem",
                borderRadius: "12px",
                border: "1px solid rgba(239, 68, 68, 0.4)",
                background: "rgba(239, 68, 68, 0.08)",
                color: "var(--color-danger)",
                marginBottom: "1rem",
              }}
            >
              Failed to load EMA analysis: {error}
            </div>
          )}

          {!loading && !error && (
            <div
              style={{
                overflowX: "auto",
                borderRadius: "14px",
                border: "1px solid rgba(148, 163, 184, 0.2)",
                background: "rgba(15, 23, 42, 0.55)",
              }}
            >
              <table
                className="data-table"
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                }}
              >
                <colgroup>
                <col style={{ width: "120px" }} />
                <col style={{ width: "260px" }} />
                <col style={{ width: "160px" }} />
                  {activePeriods.map((ema) => (
                    <col key={ema} style={{ width: "120px" }} />
                  ))}
                </colgroup>
                <thead>
                  <tr>
                    <th
                      style={{
                        textAlign: "left",
                        padding: "0.85rem 1rem",
                        fontSize: "0.8rem",
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        color: "var(--color-text-muted)",
                      }}
                    >
                      Symbol
                    </th>
                    <th
                      style={{
                        textAlign: "left",
                        padding: "0.85rem 1rem",
                        fontSize: "0.8rem",
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        color: "var(--color-text-muted)",
                      }}
                    >
                      Sector
                    </th>
                    <th
                      style={{
                        textAlign: "right",
                        padding: "0.85rem 1rem",
                        fontSize: "0.8rem",
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        color: "var(--color-text-muted)",
                      }}
                    >
                      Current Price
                    </th>
                    {activePeriods.map((ema) => (
                      <th
                        key={ema}
                        style={{
                          textAlign: "center",
                          padding: "0.85rem 0.6rem",
                          fontSize: "0.8rem",
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                          color: "var(--color-text-muted)",
                        }}
                      >
                        EMA {ema}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pagedRows.length === 0 ? (
                    <tr>
                      <td colSpan={3 + activePeriods.length} style={{ textAlign: "center", padding: "1.5rem" }}>
                        No stocks match the selected filters.
                      </td>
                    </tr>
                  ) : (
                    pagedRows.map((row, idx) => (
                      <tr
                        key={row.symbol}
                        style={{
                          background: idx % 2 === 0 ? "rgba(255, 255, 255, 0.02)" : "transparent",
                        }}
                      >
                        <td style={{ fontWeight: 700, padding: "0.8rem 1rem" }}>
                          {row.symbol}
                          <IndexBadges indices={row.indices ?? []} style={{ marginLeft: 5, verticalAlign: "middle" }} />
                        </td>
                        <td
                          style={{
                            padding: "0.8rem 1rem",
                            color: "var(--color-text-muted)",
                            maxWidth: "260px",
                            whiteSpace: "normal",
                          }}
                        >
                          {row.sector || "—"}
                        </td>
                        <td style={{ textAlign: "right", padding: "0.8rem 1rem" }}>
                          Rs. {formatNumber(row.price)}
                        </td>
                        {activePeriods.map((ema) => {
                          const status = row.ema[ema.toString()];
                          if (!status) {
                            return (
                              <td
                                key={ema}
                                style={{ textAlign: "center", color: "var(--color-text-muted)", padding: "0.8rem 0.6rem" }}
                              >
                                N/A
                              </td>
                            );
                          }
                          const style = statusStyle(status.status);
                          return (
                            <td key={ema} style={{ textAlign: "center", padding: "0.8rem 0.6rem" }}>
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  minWidth: "64px",
                                  padding: "0.2rem 0.6rem",
                                  borderRadius: "999px",
                                  background: style.bg,
                                  color: style.color,
                                  border: `1px solid ${style.border}`,
                                  fontWeight: 700,
                                  fontSize: "0.75rem",
                                  letterSpacing: "0.03em",
                                  textTransform: "uppercase",
                                }}
                              >
                                {status.status}
                              </span>
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {!loading && !error && totalPages > 1 && (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "0.75rem", marginTop: "1rem" }}>
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{
                  padding: "0.4rem 0.9rem", borderRadius: "8px", fontWeight: 600, cursor: page === 1 ? "default" : "pointer",
                  border: "1px solid rgba(148, 163, 184, 0.3)", background: "rgba(15, 23, 42, 0.4)",
                  color: page === 1 ? "rgba(148,163,184,0.35)" : "var(--color-text)",
                }}
              >
                ← Prev
              </button>
              <span style={{ fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                {page} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                style={{
                  padding: "0.4rem 0.9rem", borderRadius: "8px", fontWeight: 600, cursor: page === totalPages ? "default" : "pointer",
                  border: "1px solid rgba(148, 163, 184, 0.3)", background: "rgba(15, 23, 42, 0.4)",
                  color: page === totalPages ? "rgba(148,163,184,0.35)" : "var(--color-text)",
                }}
              >
                Next →
              </button>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
